import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { GoalSortingMode, OrderDirection } from 'shared/types/sorting-mode';

const isGoalValueEmpty = (goal: Goal, sortMode: GoalSortingMode): boolean => {
  switch (sortMode) {
    case GoalSortingMode.Impact:
      return goal.impact == null;
    case GoalSortingMode.StartDate:
      return !goal.startDate;
    case GoalSortingMode.Deadline:
      return !goal.deadline;
    case GoalSortingMode.Progress:
      return !goal.metric?.id;
    case GoalSortingMode.LifeArea:
      return !goal.lifeAreaId;
    case GoalSortingMode.Tasks:
    case GoalSortingMode.Name:
    case GoalSortingMode.Custom:
    default:
      return false;
  }
};

/**
 * Given goals already sorted by `sortMode` in their natural order, returns an
 * id list that respects `direction` while always keeping goals with no value
 * for the sort mode at the bottom of the list. This avoids the surprising
 * "empty values jump to the top" effect that a naive full-array reversal
 * causes when toggling direction.
 */
export const getOrderedGoalIds = (
  sortedGoals: Goal[],
  sortMode: GoalSortingMode | undefined,
  direction: OrderDirection,
): ID[] => {
  const effectiveSortMode = sortMode ?? GoalSortingMode.StartDate;
  const withValue: Goal[] = [];
  const withoutValue: Goal[] = [];

  sortedGoals.forEach((goal) => {
    if (isGoalValueEmpty(goal, effectiveSortMode)) {
      withoutValue.push(goal);
    } else {
      withValue.push(goal);
    }
  });

  const ordered =
    direction === OrderDirection.Desc ? withValue.toReversed() : withValue;

  return [...ordered, ...withoutValue].map(({ id }) => id);
};
